import { useState, useEffect, useCallback } from 'react';
import '../styles/AdminReportes.css';

// ── Helpers de fecha ────────────────────────────────────────────────────────

const toISO = (d) => {
  const dd   = String(d.getDate()).padStart(2, '0');
  const mm   = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const startOfMonth = (d = new Date()) =>
  new Date(d.getFullYear(), d.getMonth(), 1);

const endOfPrevMonth = (d = new Date()) =>
  new Date(d.getFullYear(), d.getMonth(), 0);

const startOfPrevMonth = (d = new Date()) =>
  new Date(d.getFullYear(), d.getMonth() - 1, 1);

const addDays = (d, n) => new Date(d.getTime() + n * 86400000);

// ── Presets ─────────────────────────────────────────────────────────────────

const PERIODOS = [
  { id: 'hoy',         label: 'Hoy' },
  { id: 'semana',      label: 'Esta semana' },
  { id: 'mes',         label: 'Este mes' },
  { id: 'mes_anterior',label: 'Mes anterior' },
  { id: 'custom',      label: 'Personalizado' },
];

function rangoDePreset(id) {
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  switch (id) {
    case 'hoy':          return { desde: hoy, hasta: hoy };
    case 'semana':       return { desde: addDays(hoy, -6), hasta: hoy };
    case 'mes':          return { desde: startOfMonth(hoy), hasta: hoy };
    case 'mes_anterior': return { desde: startOfPrevMonth(hoy), hasta: endOfPrevMonth(hoy) };
    default:             return { desde: startOfMonth(hoy), hasta: hoy };
  }
}

// ── Formateo ─────────────────────────────────────────────────────────────────

const fmt = (n) => Math.round(n).toLocaleString('es-AR');
const fmtLabel = (fechaDDMMYYYY) => {
  const [d, m] = fechaDDMMYYYY.split('/');
  const meses = ['ene','feb','mar','abr','may','jun','jul','ago','sep','oct','nov','dic'];
  return `${d}/${meses[parseInt(m, 10) - 1]}`;
};

// Convierte dd/mm/yyyy → yyyy-mm-dd para <input type="date">
const toInputDate = (ddmmyyyy) => {
  const [d, m, y] = ddmmyyyy.split('/');
  return `${y}-${m}-${d}`;
};

// Convierte yyyy-mm-dd → dd/mm/yyyy
const fromInputDate = (yyyymmdd) => {
  const [y, m, d] = yyyymmdd.split('-');
  return `${d}/${m}/${y}`;
};

// ── Gráfico de barras vertical (CSS puro) ────────────────────────────────────

function GraficoBarras({ datos }) {
  if (!datos || datos.length === 0) {
    return <p className="rep-vacio">Sin ventas en este período</p>;
  }
  const maxVal = Math.max(...datos.map(d => d.total), 1);
  return (
    <div className="rep-grafico-wrap">
      <div className="rep-barras">
        {datos.map((d, i) => (
          <div key={i} className="rep-barra-col">
            <span className="rep-barra-valor">${fmt(d.total)}</span>
            <div
              className="rep-barra"
              style={{ height: `${Math.max((d.total / maxVal) * 100, 2)}%` }}
              title={`${d.fecha}: $${fmt(d.total)} (${d.cantidad} venta${d.cantidad !== 1 ? 's' : ''})`}
            />
            <span className="rep-barra-label">{fmtLabel(d.fecha)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Barras horizontales (métodos / canal) ────────────────────────────────────

function BarrasHorizontales({ datos, colorClass }) {
  const max = Math.max(...datos.map(d => d.valor), 1);
  return (
    <div className="rep-hbars">
      {datos.filter(d => d.valor > 0).map((d, i) => (
        <div key={i} className="rep-hbar-row">
          <span className="rep-hbar-label">{d.label}</span>
          <div className="rep-hbar-track">
            <div
              className={`rep-hbar-fill ${colorClass || ''}`}
              style={{ width: `${(d.valor / max) * 100}%` }}
            />
          </div>
          <span className="rep-hbar-val">${fmt(d.valor)}</span>
        </div>
      ))}
    </div>
  );
}

// ── Componente principal ─────────────────────────────────────────────────────

const AdminReportes = ({ isOpen, onClose }) => {
  const [periodo, setPeriodo] = useState('mes');
  const [customDesde, setCustomDesde] = useState('');
  const [customHasta, setCustomHasta] = useState('');
  const [datos, setDatos]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');

  const cargarReporte = useCallback(async (pid, cDesde, cHasta) => {
    setLoading(true);
    setError('');
    try {
      let desde, hasta;
      if (pid === 'custom') {
        if (!cDesde || !cHasta) { setLoading(false); return; }
        desde = cDesde;
        hasta = cHasta;
      } else {
        const rango = rangoDePreset(pid);
        desde = toISO(rango.desde);
        hasta = toISO(rango.hasta);
      }
      const res = await fetch(`/api/reportes?desde=${desde}&hasta=${hasta}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error al cargar reporte');
      setDatos(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Cargar al abrir o cambiar período
  useEffect(() => {
    if (!isOpen) return;
    if (periodo !== 'custom') {
      cargarReporte(periodo);
    }
  }, [isOpen, periodo, cargarReporte]);

  // Reset al cerrar
  useEffect(() => {
    if (!isOpen) {
      setDatos(null);
      setError('');
      setPeriodo('mes');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const { resumen, ventasPorDia, ventasPorMetodo, topProductos, porCanal } = datos || {};

  const metodosDatos = ventasPorMetodo
    ? [
        { label: '💵 Efectivo',      valor: ventasPorMetodo.efectivo      || 0 },
        { label: '🏦 Transferencia', valor: ventasPorMetodo.transferencia  || 0 },
        { label: '💳 Débito',        valor: ventasPorMetodo.debito         || 0 },
        { label: '🏧 Crédito',       valor: ventasPorMetodo.credito        || 0 },
        { label: '🌐 Online',        valor: ventasPorMetodo.online         || 0 },
      ]
    : [];

  const canalDatos = porCanal
    ? [
        { label: '🛒 Mostrador', valor: porCanal.mostrador || 0 },
        { label: '🌐 Online',    valor: porCanal.online    || 0 },
      ]
    : [];

  return (
    <div className="rep-overlay" onClick={onClose}>
      <div className="rep-panel" onClick={e => e.stopPropagation()}>

        {/* ── Header ── */}
        <div className="rep-header">
          <div>
            <h2 className="rep-titulo">Reportes de Ventas</h2>
            {datos && (
              <p className="rep-subtitulo">{datos.desde} → {datos.hasta}</p>
            )}
          </div>
          <button className="rep-close" onClick={onClose}>✕</button>
        </div>

        {/* ── Selector de período ── */}
        <div className="rep-periodos">
          {PERIODOS.map(p => (
            <button
              key={p.id}
              className={`rep-periodo-btn ${periodo === p.id ? 'activo' : ''}`}
              onClick={() => setPeriodo(p.id)}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Fechas personalizadas */}
        {periodo === 'custom' && (
          <div className="rep-custom-fechas">
            <div className="rep-fecha-group">
              <label>Desde</label>
              <input
                type="date"
                value={customDesde}
                onChange={e => setCustomDesde(fromInputDate(e.target.value))}
              />
            </div>
            <div className="rep-fecha-group">
              <label>Hasta</label>
              <input
                type="date"
                value={customHasta}
                onChange={e => setCustomHasta(fromInputDate(e.target.value))}
              />
            </div>
            <button
              className="rep-btn-aplicar"
              onClick={() => cargarReporte('custom', customDesde, customHasta)}
              disabled={!customDesde || !customHasta}
            >
              Aplicar
            </button>
          </div>
        )}

        {/* ── Contenido ── */}
        <div className="rep-body">

          {loading && (
            <div className="rep-loading">
              <span className="rep-spinner" />
              Cargando reporte...
            </div>
          )}

          {error && <div className="rep-error">⚠️ {error}</div>}

          {!loading && datos && (
            <>
              {/* KPI cards */}
              <div className="rep-kpis">
                <div className="rep-kpi">
                  <span className="rep-kpi-icon">💰</span>
                  <span className="rep-kpi-valor">${fmt(resumen.totalVentas)}</span>
                  <span className="rep-kpi-label">Total vendido</span>
                </div>
                <div className="rep-kpi">
                  <span className="rep-kpi-icon">🧾</span>
                  <span className="rep-kpi-valor">{resumen.cantidadVentas}</span>
                  <span className="rep-kpi-label">Ventas</span>
                </div>
                <div className="rep-kpi">
                  <span className="rep-kpi-icon">📦</span>
                  <span className="rep-kpi-valor">{resumen.unidadesVendidas}</span>
                  <span className="rep-kpi-label">Unidades</span>
                </div>
                <div className="rep-kpi">
                  <span className="rep-kpi-icon">📈</span>
                  <span className="rep-kpi-valor">${fmt(resumen.ticketPromedio)}</span>
                  <span className="rep-kpi-label">Ticket prom.</span>
                </div>
              </div>

              {/* Ventas por día */}
              <section className="rep-seccion">
                <h3 className="rep-seccion-titulo">Ventas por día</h3>
                <GraficoBarras datos={ventasPorDia} />
              </section>

              {/* Grid: métodos + canal */}
              <div className="rep-grid-2">
                <section className="rep-seccion">
                  <h3 className="rep-seccion-titulo">Por método de pago</h3>
                  {metodosDatos.every(d => d.valor === 0)
                    ? <p className="rep-vacio">Sin datos</p>
                    : <BarrasHorizontales datos={metodosDatos} />
                  }
                </section>
                <section className="rep-seccion">
                  <h3 className="rep-seccion-titulo">Por canal</h3>
                  {canalDatos.every(d => d.valor === 0)
                    ? <p className="rep-vacio">Sin datos</p>
                    : <BarrasHorizontales datos={canalDatos} colorClass="rep-hbar-canal" />
                  }
                </section>
              </div>

              {/* Top productos */}
              <section className="rep-seccion">
                <h3 className="rep-seccion-titulo">Productos más vendidos</h3>
                {topProductos.length === 0 ? (
                  <p className="rep-vacio">Sin datos</p>
                ) : (
                  <div className="rep-tabla-wrap">
                    <table className="rep-tabla">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Producto</th>
                          <th>Unidades</th>
                          <th>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topProductos.map((p, i) => (
                          <tr key={i}>
                            <td className="rep-td-num">{i + 1}</td>
                            <td>{p.nombre}</td>
                            <td className="rep-td-num">{p.unidades}</td>
                            <td className="rep-td-monto">${fmt(p.total)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            </>
          )}

          {!loading && !datos && !error && (
            <p className="rep-vacio rep-vacio-center">Seleccioná un período para ver el reporte</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminReportes;
